
import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Mic, Volume2, VolumeX, Music, Speaker } from 'lucide-react';
import DirectComputerAudioStreaming from './DirectComputerAudioStreaming';

const LiveSessionAudioMixer = () => {
  const [masterVolume, setMasterVolume] = useState(75);
  const [inputGain, setInputGain] = useState(70);
  const [participants, setParticipants] = useState([
    { id: 1, name: 'You', isMuted: false, volume: 80 },
    { id: 2, name: 'Sarah', isMuted: false, volume: 75 },
    { id: 3, name: 'Mike', isMuted: true, volume: 65 },
    { id: 4, name: 'Alex', isMuted: false, volume: 70 },
  ]);
  const [isSystemAudioActive, setIsSystemAudioActive] = useState(false);

  const toggleMute = (id: number) => {
    setParticipants(prev => prev.map(p => 
      p.id === id ? { ...p, isMuted: !p.isMuted } : p
    ));
  };

  const updateVolume = (id: number, volume: number) => {
    setParticipants(prev => prev.map(p => 
      p.id === id ? { ...p, volume } : p
    ));
  };

  const handleToggleSystemAudio = (isActive: boolean) => {
    setIsSystemAudioActive(isActive);
  };

  return (
    <div className="space-y-6 p-4 bg-gradient-to-b from-[#151520] to-[#0f0f1a]">
      <h3 className="text-xl font-semibold flex items-center gap-2 mb-4 text-white drop-shadow-neon-text">
        <Speaker className="h-5 w-5 text-neon-purple" />
        Audio Mixer
      </h3>

      {/* Computer Audio Streaming */}
      <DirectComputerAudioStreaming 
        isActive={isSystemAudioActive}
        onToggle={handleToggleSystemAudio}
      />

      {/* Master Volume */}
      <Card className="p-4 border border-neon-purple/30 bg-gradient-to-r from-black/70 to-neon-purple/10 shadow-neon-purple/20 backdrop-blur-sm">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium flex items-center gap-2 text-white">
              <Volume2 className="h-4 w-4 text-neon-purple" />
              Master Volume
            </h4>
            <span className="text-xs text-white/70 font-mono">{masterVolume}%</span>
          </div>
          <Slider
            value={[masterVolume]}
            min={0}
            max={100}
            step={1}
            onValueChange={(vals) => setMasterVolume(vals[0])}
            className="mt-2"
          />
        </div>
      </Card>

      {/* Microphone Input */}
      <Card className="p-4 border border-neon-purple/30 bg-gradient-to-r from-black/70 to-neon-purple/10 shadow-neon-purple/20 backdrop-blur-sm">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium flex items-center gap-2 text-white">
              <Mic className="h-4 w-4 text-neon-purple" />
              Input Gain
            </h4>
            <span className="text-xs text-white/70 font-mono">{inputGain}%</span>
          </div>
          <Slider
            value={[inputGain]}
            min={0}
            max={100}
            step={1}
            onValueChange={(vals) => setInputGain(vals[0])}
            className="mt-2"
          />
        </div>
      </Card>

      {/* Participants */}
      <h4 className="text-sm font-medium mt-6 mb-3 text-white drop-shadow-neon-text flex items-center gap-2">
        <Volume2 className="h-4 w-4 text-neon-purple" />
        Participant Volumes
      </h4>
      <div className="space-y-4">
        {participants.map((participant) => (
          <Card key={participant.id} className="p-4 border border-neon-purple/20 bg-gradient-to-r from-black/70 to-neon-purple/10 shadow-neon-purple/10 backdrop-blur-sm hover:border-neon-purple/40 hover:shadow-neon-purple/30 transition-all duration-300">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleMute(participant.id)}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    {participant.isMuted ? (
                      <VolumeX className="h-4 w-4 text-red-500" />
                    ) : (
                      <Volume2 className="h-4 w-4 text-neon-green" />
                    )}
                  </button>
                  <h4 className="text-sm font-medium text-white">{participant.name}</h4>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor={`mute-${participant.id}`} className="text-xs mr-2 text-white/70">
                    Mute
                  </Label>
                  <Switch
                    id={`mute-${participant.id}`}
                    checked={participant.isMuted}
                    onCheckedChange={() => toggleMute(participant.id)}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <VolumeX className="h-3 w-3 text-white/50" />
                <Slider
                  value={[participant.volume]}
                  min={0}
                  max={100}
                  step={1}
                  disabled={participant.isMuted}
                  onValueChange={(vals) => updateVolume(participant.id, vals[0])}
                  className={participant.isMuted ? "opacity-50" : ""}
                />
                <Volume2 className="h-3 w-3 text-white/50" />
              </div>
              
              {/* Volume level visualizer */}
              {!participant.isMuted && (
                <div className="h-2 bg-black/40 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-neon-purple to-neon-blue" 
                    style={{ width: `${participant.volume}%` }}
                  />
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default LiveSessionAudioMixer;
